import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, KeyRound, Loader2, Clock, CreditCard, Users, ShieldCheck, ArrowRight, Gift, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePricing, aiExtensions } from "@/lib/pricing";
import InvoiceOrderDialog from "@/components/InvoiceOrderDialog";

export default function AiLock({ feature = "die KI-Funktionen", onUnlocked }) {
  const { user, refreshUser } = useAuth();
  const pricing = usePricing();
  const plan = user?.license_plan === "team" ? "team" : "einzel";
  const isTeam = plan === "team";
  const extensions = aiExtensions(pricing, plan);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [selectedDays, setSelectedDays] = useState(extensions[0]?.days || 30);
  const [paying, setPaying] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  useEffect(() => {
    api.get("/payments/active-discount")
      .then((r) => { if (r.data?.active) setDiscount(r.data); })
      .catch(() => {});
  }, []);

  const redeem = async () => {
    const c = code.trim();
    if (!c) {
      toast.error("Bitte geben Sie einen KI-Verlängerungs-Code ein.");
      return;
    }
    setRedeeming(true);
    try {
      const r = await api.post("/auth/extend-ai", { code: c });
      toast.success(`KI-Zugang um ${r.data.days_added} Tage verlängert.`);
      setCode("");
      await refreshUser();
      onUnlocked?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRedeeming(false);
    }
  };

  const checkout = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "ai_extension",
        plan,
        days: selectedDays,
        origin_url: window.location.origin,
        cancel_path: window.location.pathname,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPaying(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-xl mx-auto px-5 sm:px-8 py-12 sm:py-16"
      data-testid="ai-lock"
    >
      <div className="rounded-3xl glass-strong p-8 sm:p-10 text-center">
        <p className="text-[11px] uppercase tracking-[0.18em] text-gold/80 mb-5" data-testid="ai-lock-academy">
          LUX Lighting Academy · Lichtplanungskurs für Architekten
        </p>
        <div className="relative mx-auto h-16 w-16 rounded-2xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-6">
          <Lock className="h-7 w-7 text-gold" />
          <div className="absolute inset-0 blur-xl bg-gold/20 -z-10 rounded-2xl" />
        </div>

        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl">KI-Zugang abgelaufen</h2>
        <p className="text-foreground/70 mt-3 max-w-md mx-auto leading-relaxed">
          Ihr 30-tägiger KI-Zugang ist abgelaufen. Verlängern Sie ihn, um {feature} weiter zu nutzen.
          Ihr gesamter Kursinhalt, Ihre Notizen und Ihr Fortschritt bleiben unverändert verfügbar.
        </p>

        {isTeam && (
          <div className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-5 text-left" data-testid="ai-lock-team-notice">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-gold" />
              <h3 className="font-heading font-bold text-lg">Team-Lizenz · zentral verwaltet</h3>
            </div>
            <p className="text-sm text-foreground/75 leading-relaxed">
              Die KI-Laufzeit Ihrer Team-Lizenz wird zentral über Ihr Büro verwaltet. Eine Zahlung verlängert
              den KI-Coach für <strong>alle</strong> registrierten Teammitglieder gleichzeitig.
            </p>
          </div>
        )}

        <div className="mt-8">
          <p className="text-sm font-medium text-foreground/80 mb-3">Wählen Sie Ihr Verlängerungs-Paket:</p>
          {discount && (
            <div
              data-testid="ai-lock-discount-badge"
              className="mb-4 flex items-center gap-2.5 rounded-xl border border-gold/40 bg-gold/[0.12] px-4 py-2.5 text-left"
            >
              <Gift className="h-4 w-4 text-gold flex-shrink-0" />
              <p className="text-sm text-foreground/90">
                <strong className="text-gold">{discount.percent}% Willkommen-zurück-Rabatt</strong> aktiv – er wird
                automatisch berücksichtigt.
              </p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2.5">
            {extensions.map((x) => {
              const active = selectedDays === x.days;
              return (
                <button
                  key={x.days}
                  type="button"
                  onClick={() => setSelectedDays(x.days)}
                  data-testid={`ai-lock-package-${x.days}`}
                  aria-pressed={active}
                  className={`p-3 rounded-xl border text-center transition-all duration-300 ${
                    active ? "bg-gold/15 border-gold/50" : "bg-white/5 border-white/10 hover:bg-white/[0.08]"
                  }`}
                >
                  <Clock className={`h-4 w-4 mx-auto mb-1 ${active ? "text-gold" : "text-muted-foreground"}`} />
                  <div className="font-heading font-bold text-sm">{x.days} Tage</div>
                  <div className="text-gold text-xs mt-0.5">{x.price}</div>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Alle Preise exkl. MWST.</p>
          <button
            onClick={isTeam ? () => setInvoiceOpen(true) : checkout}
            disabled={paying}
            data-testid="ai-lock-checkout-button"
            className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
          >
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : isTeam ? <FileText className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
            {paying ? "Weiterleitung zur Zahlung…" : isTeam ? "KI fürs Team verlängern (auf Rechnung)" : "Jetzt bezahlen & verlängern"}
            {!paying && <ArrowRight className="h-4 w-4" />}
          </button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-gold" /> {isTeam ? "Schweizer QR-Rechnung via AbaNinja · 14 Tage" : "Sichere Zahlung über Stripe"}
          </p>
        </div>

        {!isTeam && (
          <div className="mt-8 pt-7 border-t hairline">
            <p className="text-sm text-muted-foreground mb-3 flex items-center justify-center gap-2">
              <KeyRound className="h-4 w-4 text-gold" /> Schon einen Verlängerungs-Code? Hier einlösen:
            </p>
            <div className="flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
                placeholder="LUXAI-XXXXX-XXXXX-XXXXX"
                data-testid="ai-lock-redeem-input"
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-gold/50 focus:ring-2 focus:ring-gold/20 transition-all text-foreground placeholder:text-muted-foreground/50 font-mono text-sm uppercase"
              />
              <button
                onClick={redeem}
                disabled={redeeming}
                data-testid="ai-lock-redeem-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl glass hover:glow-gold transition-all disabled:opacity-60"
              >
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4 text-gold" />}
                Einlösen
              </button>
            </div>
          </div>
        )}
      </div>
      <InvoiceOrderDialog open={invoiceOpen} onOpenChange={setInvoiceOpen} mode="ai_extension" days={selectedDays} />
    </motion.div>
  );
}
